import { NgClass, NgStyle } from '@angular/common';
import { Component, computed, HostListener, inject } from '@angular/core';
import { TourService } from '../tour.service';

// Peças do confete da comemoração (posição/cor/atraso fixos por índice: sem Math.random no render).
const CONFETTI_COLORS = ['#696cff', '#71dd37', '#ffab00', '#ff3e1d', '#03c3ec', '#8592a3'];
const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: (i % 7) * 0.06,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  rotate: (i * 53) % 360,
  drift: ((i % 5) - 2) * 18,
}));

// Overlay do tour: bloqueio da página, spotlight (furo via box-shadow gigante) sobre o elemento do
// passo, card do passo, convite de primeira visita e comemoração ao concluir. Montado uma vez no
// master-page; tudo dirigido pelos signals do TourService.
@Component({
  selector: 'app-tour-overlay',
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss',
  imports: [NgStyle, NgClass],
})
export class TourOverlayComponent {
  protected readonly tour = inject(TourService);
  protected readonly confetti = CONFETTI;

  // Passo "Sua vez": em vez de um backdrop inteiro, quatro faixas bloqueiam tudo MENOS o furo —
  // o clique no elemento destacado chega na tela de verdade.
  protected readonly blockers = computed(() => {
    const rect = this.tour.spotlight();
    if (!rect || !this.tour.awaitingInteraction()) return null;
    const right = rect.left + rect.width;
    const bottom = rect.top + rect.height;
    return [
      { top: '0', left: '0', right: '0', height: `${Math.max(0, rect.top)}px` },
      { top: `${bottom}px`, left: '0', right: '0', bottom: '0' },
      { top: `${rect.top}px`, left: '0', width: `${Math.max(0, rect.left)}px`, height: `${rect.height}px` },
      { top: `${rect.top}px`, left: `${right}px`, right: '0', height: `${rect.height}px` },
    ];
  });

  // Card: ao lado do spotlight quando cabe (direita > esquerda > abaixo > acima), senão central. No
  // mobile (<576px) é sempre bottom-sheet. Altura estimada — suficiente pro flip.
  protected readonly popStyle = computed<Record<string, string>>(() => {
    const rect = this.tour.spotlight();
    // Dependência explícita do passo: reposiciona quando o conteúdo troca, não só o rect.
    this.tour.stepIndex();

    if (typeof window === 'undefined') return {};
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (vw < 576) return { left: '0.75rem', right: '0.75rem', bottom: '0.75rem' };
    if (!rect) return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

    const width = 400;
    const height = 280;
    const gap = 18;
    const clampTop = (top: number) => Math.max(16, Math.min(top, vh - height - 16));
    const clampLeft = (left: number) => Math.max(16, Math.min(left, vw - width - 16));

    // Elemento baixo e largo (card, faixa de totais): abaixo ou acima fica mais natural.
    const wide = rect.width > vw * 0.45;
    if (!wide) {
      if (rect.left + rect.width + gap + width <= vw - 16) {
        return { left: `${rect.left + rect.width + gap}px`, top: `${clampTop(rect.top)}px` };
      }
      if (rect.left - gap - width >= 16) {
        return { left: `${rect.left - gap - width}px`, top: `${clampTop(rect.top)}px` };
      }
    }
    const below = rect.top + rect.height + gap;
    if (below + height <= vh) return { left: `${clampLeft(rect.left)}px`, top: `${below}px` };
    const above = rect.top - gap - height;
    if (above >= 0) return { left: `${clampLeft(rect.left)}px`, top: `${above}px` };
    return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  });

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.tour.activeTour()) this.tour.measure();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.tour.activeTour()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.tour.dismiss();
    } else if (event.key === 'ArrowRight' || (event.key === 'Enter' && !this.tour.awaitingInteraction())) {
      event.preventDefault();
      this.tour.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.tour.prev();
    }
  }
}
